import { useAssetStore } from '@/stores/api/asset';
import { composer } from '@/threejs/core';
import { meshLoader } from '@/threejs/utils/MeshLoader';
import * as THREE from 'three';
import { BaseObject, applyProperties } from './base';

export class Model extends BaseObject {
  constructor(createProperties) {
    super();
    this.userData = {
      ...this.userData,
      ...createProperties.data,
      type: 'model',
    };

    this.isLoaded = false;

    const modelType = createProperties.data.ext;
    const url = createProperties.data.url;

    if (url) {
      this.loadModel(url, modelType, this);
    }

    this.select = () => {
      this.userData.selected = true;
    };
    this.clearSelection = () => {
      this.userData.selected = false;
    };
  }

  applyProperties(newProperties) {
    applyProperties(this, newProperties, false);
    if (this.isLoaded) {
      this.resetMaterials();
      if (newProperties.uvAssets) {
        newProperties.uvAssets.forEach((uvAsset) => {
          this.loadMaterialForUV(uvAsset);
        });
      }

      // this.getOutline()

      window.dispatchEvent(new CustomEvent('render'));
    }

    if ((newProperties.url && newProperties.url !== this.userData.data.url) || !this.isLoaded) {
      const modelType = newProperties.ext;
      const url = newProperties.url;
      this.loadModel(url, modelType, this);
    }
  }

  loaded(object) {
    this.isLoaded = true;
    if (this.userData.data.uvAssets) {
      this.userData.data.uvAssets.forEach((uvAsset) => {
        this.loadMaterialForUV(uvAsset);
      });
    }
    this.getOutline();

    window.dispatchEvent(new CustomEvent('render'));
    window.dispatchEvent(new CustomEvent('modelLoaded', { detail: { code: this.userData.code } }));
  }

  // Helper function to create custom material for non-standard UV channels
  createCustomUVMaterial(texture, uvChannelName, geometry) {
    // Check if the UV channel exists
    if (!geometry.attributes[uvChannelName]) {
      console.warn(
        `UV channel '${uvChannelName}' not found in geometry. Available UV channels:`,
        Object.keys(geometry.attributes).filter((key) => key.toLowerCase().includes('uv'))
      );
      return new THREE.MeshStandardMaterial({ map: texture });
    }

    // Create a shader material that uses the specified UV channel
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTexture: { value: texture },
        uOpacity: { value: 1.0 },
      },
      vertexShader: `
      attribute vec2 ${uvChannelName};
      varying vec2 vUv;

      void main() {
        vUv = ${uvChannelName};
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
      fragmentShader: `
      uniform sampler2D uTexture;
      uniform float uOpacity;
      varying vec2 vUv;

      void main() {
        vec4 texColor = texture2D(uTexture, vUv);
        gl_FragColor = vec4(texColor.rgb, texColor.a * uOpacity);
      }
    `,
      transparent: true,
      side: THREE.DoubleSide, // Allow viewing from both sides
    });

    return material;
  }

  async loadMaterialForUV(uvAssets) {
    // Load the material for the given UV assets

    const assetStore = useAssetStore();

    // Loop through each UV channel in the uvAssets object
    Object.keys(uvAssets).forEach(async (meshName) => {
      const mesh = this.getObjectByName(meshName);

      const uvAsset = uvAssets[meshName];

      const asset = await assetStore.fetch(uvAsset.resource_id);
      if (!asset) {
        console.warn(`Asset not found for Mesh Name ${meshName}:`, uvAsset.resource_id);
        return;
      }

      const uvChannelName = uvAsset.uv_channel;
      if (!uvChannelName) return;

      // load asset.data.original_url and add it to the object's material using specific UV channel
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(
        asset.latest_version.file,
        (texture) => {
          mesh.userData.originalMaterial = mesh.material;

          texture.colorSpace = THREE.SRGBColorSpace;
          /// flip texture vertically to match three.js UV space
          texture.flipY = false;
          // Clone the existing material to preserve other properties
          let material = new THREE.MeshStandardMaterial();

          // For different UV channels, apply texture appropriately
          if (uvChannelName === 'uv' || uvChannelName === 'uv1') {
            // Primary UV channel - use as main texture map
            material.map = texture;
          } else if (uvChannelName === 'uv2') {
            // Second UV channel - could be used for lightmaps, detail textures, etc.
            // You can choose what to do with this channel based on your needs
            material.map = texture; // or material.lightMap = texture
          } else {
            // For custom UV channels, create a custom shader material
            material = this.createCustomUVMaterial(texture, uvChannelName, mesh.geometry);
          }

          mesh.material = material;
          mesh.material.needsUpdate = true;
          composer.render();
        },
        undefined,
        (error) => {
          console.error(`Error loading texture for UV channel ${uvChannelName}:`, error, asset.data.original_url);
        }
      );
    });
  }

  resetMaterials() {
    this.traverse((child) => {
      if (child.isMesh) {
        if (child.userData.originalMaterial) {
          child.material = child.userData.originalMaterial;
          child.material.needsUpdate = true;
        }
      }
    });
    composer.render();
  }

  async loadModel(url, modelType, group) {
    try {
      const loadedObject = await meshLoader.load(url, modelType);

      // Apply custom processing for materials and outline
      loadedObject.traverse((child) => {
        this.alterChildMesh(child);
      });

      group.add(loadedObject);
      this.loaded(loadedObject);
    } catch (error) {
      console.error('Error loading model:', error);
    }
  }

  alterChildMesh(child) {
    if (child.isMesh) {
      const g = child.geometry;
      if (!g.getAttribute('normal')) g.computeVertexNormals();
      child.castShadow = true;
      child.receiveShadow = true;
    }
  }
}
